/**
 * component-facets.js
 * Progressive enhancement for collection filtering, sorting and pagination.
 *
 * Everything this file upgrades already works without it: the filter form is a
 * real GET form with a submit button, sort is a real form, and pagination is
 * real links. This script replaces full page loads with section re-renders —
 * it never becomes the only way to operate the page.
 *
 * The server stays the single source of truth for markup. Filter counts,
 * disabled states, the result count and the grid all come back rendered from
 * Liquid rather than being recomputed client-side, so there is exactly one
 * implementation of the filtering rules.
 */
(function () {
  'use strict';

  // Single-execution guard — see component-drawer.js. This file registers
  // document-level change/submit/click listeners; a duplicate registration
  // would fire two section fetches per filter change.
  if (window.__themeFacetsLoaded) return;
  window.__themeFacetsLoaded = true;

  const root = document.querySelector('[data-collection-root]');
  if (!root) return;

  // Read from the DOM, never hardcoded. The Section Rendering API expects the
  // section's ID as assigned by the JSON template ("product-grid"), not the
  // section type ("main-collection"). Getting this wrong 404s every request
  // and silently degrades to full page loads.
  const SECTION_ID = root.getAttribute('data-collection-root');
  if (!SECTION_ID) return;

  /**
   * Renders a filtered/sorted/paginated URL into the page.
   *
   * @param {string} url Full destination URL.
   * @param {boolean} append True for "load more" — keeps existing grid items.
   */
  function render(url, append) {
    setBusy(true);

    window.Theme.fetchSection(SECTION_ID, url)
      .then((html) => {
        const parsed = new DOMParser().parseFromString(html, 'text/html');

        const newGrid = parsed.querySelector('[data-product-grid]');
        const currentGrid = document.querySelector('[data-product-grid]');
        if (!newGrid || !currentGrid) return;

        if (append) {
          // Move only the <li>s across, then replace the pagination block, so
          // scroll position is untouched and already-loaded images are not
          // re-downloaded.
          const incoming = newGrid.querySelectorAll('.collection__item');
          const list = currentGrid.querySelector('.collection__grid');
          const firstNew = incoming[0];

          incoming.forEach((item) => list && list.appendChild(item));

          const oldPagination = currentGrid.querySelector('.pagination');
          const newPagination = newGrid.querySelector('.pagination');
          if (oldPagination) oldPagination.remove();
          if (newPagination) currentGrid.appendChild(newPagination);

          // Focus the first newly added card's link. Without this, appending
          // content leaves a keyboard user's focus on a button that no longer
          // exists and drops them at the top of the document.
          if (firstNew) {
            const link = firstNew.querySelector('.card__link');
            if (link) {
              link.setAttribute('tabindex', '-1');
              link.focus({ preventScroll: true });
            }
          }
        } else {
          currentGrid.replaceWith(newGrid);
          replace(parsed, '[data-facet-form]');
          replace(parsed, '[data-active-filters]');
          replace(parsed, '[data-product-count]');
        }

        // Keep the address bar in sync so the view is shareable and the back
        // button returns to the previous filter state.
        window.history.pushState({ facets: true }, '', url);
        announce();
      })
      .catch((error) => {
        // An aborted request is the normal result of a newer one superseding
        // it, not a failure. Anything else falls back to a real navigation so
        // the user is never stranded.
        if (error.name === 'AbortError') return;
        window.location.href = url;
      })
      .finally(() => setBusy(false));
  }

  /**
   * Swaps a single element from the parsed response into the live document.
   * Handles the element being absent on either side — the active-filter chips
   * exist only when a filter is applied.
   */
  function replace(parsed, selector) {
    const incoming = parsed.querySelector(selector);
    const current = document.querySelector(selector);

    if (incoming && current) {
      current.replaceWith(incoming);
    } else if (!incoming && current) {
      current.remove();
    }
  }

  function setBusy(busy) {
    const grid = document.querySelector('[data-product-grid]');
    if (grid) grid.setAttribute('aria-busy', busy ? 'true' : 'false');
    root.classList.toggle('is-loading', busy);
  }

  function announce() {
    const count = document.querySelector('[data-product-count]');
    if (count) window.Theme.announce(count.textContent.trim());
  }

  /** Builds a URL from the filter form's current state. */
  function urlFromForm(form) {
    const params = new URLSearchParams(new FormData(form));

    // Empty price inputs would otherwise send `filter.v.price.gte=` and
    // return zero results.
    for (const [key, value] of Array.from(params.entries())) {
      if (value === '') params.delete(key);
    }

    const query = params.toString();
    return form.getAttribute('action') + (query ? '?' + query : '');
  }

  /* ------------------------------------------------------------------ *
   * Filtering
   * ------------------------------------------------------------------ */

  const debouncedFilter = window.Theme.debounce((form) => render(urlFromForm(form), false), 350);

  document.addEventListener('change', (event) => {
    const form = event.target.closest('[data-facet-form]');
    if (!form) return;

    if (event.target.matches('.facet__price-input')) {
      // Typing a price is a stream of change events; wait for a pause.
      debouncedFilter(form);
    } else {
      render(urlFromForm(form), false);
    }
  });

  // Submitting the form is the no-JS path. Intercept it so the button still
  // works when JS is present, rather than causing a full reload.
  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-facet-form]');
    if (!form) return;
    event.preventDefault();
    render(urlFromForm(form), false);

    const drawer = document.getElementById('FilterDrawer');
    if (drawer && drawer.open) drawer.close();
  });

  /* ------------------------------------------------------------------ *
   * Sorting
   * ------------------------------------------------------------------ */

  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-sort-select]');
    if (!select) return;

    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', select.value);
    url.searchParams.delete('page');
    render(url.toString(), false);
  });

  /* ------------------------------------------------------------------ *
   * Chips, clear-all, and load more
   * ------------------------------------------------------------------ */

  document.addEventListener('click', (event) => {
    const facetLink = event.target.closest('[data-facet-link]');
    if (facetLink) {
      event.preventDefault();
      render(facetLink.href, false);
      return;
    }

    const next = event.target.closest('[data-pagination-next]');
    if (next) {
      event.preventDefault();
      render(next.href, true);
    }
  });

  /* ------------------------------------------------------------------ *
   * History
   * ------------------------------------------------------------------ */

  window.addEventListener('popstate', (event) => {
    // Only handle entries this script created. Reloading on anything else
    // avoids fighting with other components' history usage.
    if (event.state && event.state.facets) {
      render(window.location.href, false);
    }
  });
})();
